import { AlertTriangle, CheckCircle2, Info, X as LinkedIn, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cx, errorMessage } from "../util";
import { GfPortal } from "./Portal";

export type ToastTone = "success" | "error" | "info" | "warn";

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** ms; 0 = stays until dismissed. Default 4.5s (errors 7s). */
  duration?: number;
  action?: { label: string; onClick: () => void };
  /** Re-using an id replaces that toast (e.g. "Saving…" → "Saved"). */
  id?: string;
}

interface ToastRecord extends ToastOptions {
  id: string;
  tone: ToastTone;
  createdAt: number;
}

export interface ToastApi {
  show: (opts: ToastOptions) => string;
  success: (title: ReactNode, description?: ReactNode) => string;
  /** Pass a caught error or a message. ApiError/Error messages are shown as-is. */
  error: (errOrTitle: unknown, description?: ReactNode) => string;
  info: (title: ReactNode, description?: ReactNode) => string;
  warn: (title: ReactNode, description?: ReactNode) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismiss = useCallback((id: string) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const show = useCallback((opts: ToastOptions) => {
    const id = opts.id ?? `t${++seq}`;
    const rec: ToastRecord = { ...opts, id, tone: opts.tone ?? "info", createdAt: Date.now() };
    setToasts((t) => {
      const without = t.filter((x) => x.id !== id);
      return [...without, rec].slice(-5);
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, tone: "success" }),
      info: (title, description) => show({ title, description, tone: "info" }),
      warn: (title, description) => show({ title, description, tone: "warn" }),
      error: (errOrTitle, description) => {
        const isNode = typeof errOrTitle === "string" || (typeof errOrTitle === "object" && errOrTitle !== null && "$$typeof" in errOrTitle);
        const title = isNode ? (errOrTitle as ReactNode) : errorMessage(errOrTitle);
        return show({ title, description, tone: "error" });
      },
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <GfPortal>
        <div className="gf-toasts" aria-live="polite" aria-relevant="additions">
          {toasts.map((t) => (
            <ToastItem key={t.id + t.createdAt} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>
      </GfPortal>
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error: <XCircle size={18} />,
  info: <Info size={18} />,
  warn: <AlertTriangle size={18} />,
};

function ToastItem({ toast, onDismiss }: { toast: ToastRecord; onDismiss: () => void }) {
  const duration = toast.duration ?? (toast.tone === "error" ? 7000 : 4500);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration);
  const startedAt = useRef(Date.now());
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (duration <= 0 || paused) return;
    startedAt.current = Date.now();
    const t = window.setTimeout(() => dismissRef.current(), Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(t);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [paused, duration]);

  return (
    <div
      className={cx("gf-toast", `gf-toast--${toast.tone}`)}
      role={toast.tone === "error" ? "alert" : "status"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="gf-toast__icon">{ICONS[toast.tone]}</span>
      <div className="gf-toast__body">
        <p className="gf-toast__title">{toast.title}</p>
        {toast.description ? <p className="gf-toast__desc">{toast.description}</p> : null}
      </div>
      {toast.action ? (
        <button
          type="button"
          className="gf-toast__action"
          onClick={() => {
            toast.action?.onClick();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      ) : null}
      <button type="button" className="gf-toast__close" aria-label="Dismiss" onClick={onDismiss}>
        <LinkedIn size={15} />
      </button>
    </div>
  );
}

/** `const toast = useToast(); toast.success("Saved"); toast.error(err);` */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() must be used inside <ToastProvider> (or <GfProvider>).");
  return ctx;
}
